import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Button,
  CircularProgress,
  Typography,
  Alert,
  Link,
  Paper,
  Grid,
  InputAdornment,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Field, Form, Formik } from 'formik';
import { toValidator } from 'renderer/shared/schemas/common.schemas';
import { TextField } from 'formik-mui';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { useRegisterMutation } from '../services/auth.endpoints';
import { RegisterRequestFormSchema } from '../schemas/auth.schemas';

const ContinueButton = () => (
  <Button
    component={RouterLink}
    to="/login"
    variant="contained"
    color="primary"
  >
    Continue
  </Button>
);

const serverUrl = process.env.SERVER_URL ?? 'http://localhost:3000';

const initialForm = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  showPassword: false,
  confirmPassword: '',
};

const Register: React.FC = () => {
  const [
    register,
    { isLoading, isSuccess, error: registerError, data: registerResponse },
  ] = useRegisterMutation();
  const [captchaValue, setCaptchaValue] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState('');
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  useEffect(() => {
    const fetchRecaptchaSiteKey = async () => {
      try {
        const response = await axios.get(`${serverUrl}/api/config`);
        setRecaptchaSiteKey(response.data.recaptchaSiteKey);
      } catch (error) {
        setCaptchaError(`Error fetching reCAPTCHA site key: ${error}`);
      }
    };

    fetchRecaptchaSiteKey();
  }, []);

  useEffect(() => {
    if (registerError && recaptchaRef.current) {
      recaptchaRef.current.reset();
      setCaptchaValue(null);
    }
  }, [registerError]);

  const handleCaptchaChange = (value: any) => {
    setCaptchaValue(value);
  };

  if (!recaptchaSiteKey) return null;
  return (
    <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
      <Paper elevation={3} sx={{ px: 5, py: 6, width: '100%', maxWidth: 400 }}>
        {/* <Typography variant="h5" fontWeight="bold" sx={{ mb: 1, textAlign: 'center' }}>
          Digital Anarchy
        </Typography> */}
        {isSuccess && registerResponse ? (
          <>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ textAlign: 'center' }}
            >
              Email Sent
            </Typography>
            <Typography sx={{ textAlign: 'center', mb: 2 }}>
              {registerResponse.message}
            </Typography>
            <Box textAlign="center">
              <ContinueButton />
            </Box>
          </>
        ) : (
          <>
            {/* <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center' }}>
              Register
            </Typography> */}
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Sign up free
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              No credit card required
            </Typography>
            <Formik
              initialValues={initialForm}
              validate={toValidator(RegisterRequestFormSchema)}
              onSubmit={(values) => {
                if (!captchaValue) {
                  setCaptchaError('Please verify that you are not a robot.');
                  return;
                }
                setCaptchaError('');
                register({ ...values, recaptchaToken: captchaValue });
              }}
            >
              {({ values, setFieldValue }) => (
                <Form>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {captchaError && (
                      <Grid item xs={12}>
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {captchaError}
                        </Alert>
                      </Grid>
                    )}
                    {registerError && (
                      <Grid item xs={12}>
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {formatQueryError(registerError)}
                        </Alert>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Field
                        component={TextField}
                        size="small"
                        name="firstName"
                        label="First Name"
                        disabled={isLoading}
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Field
                        component={TextField}
                        size="small"
                        name="lastName"
                        label="Last Name"
                        disabled={isLoading}
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Field
                        component={TextField}
                        size="small"
                        name="email"
                        type="email"
                        label="Email"
                        disabled={isLoading}
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Field
                        component={TextField}
                        name="password"
                        type={values.showPassword ? undefined : 'password'}
                        label="Password"
                        disabled={isLoading}
                        required
                        fullWidth
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip
                                title={
                                  values.showPassword
                                    ? 'Hide password'
                                    : 'Show password'
                                }
                              >
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() =>
                                    setFieldValue(
                                      'showPassword',
                                      !values.showPassword,
                                    )
                                  }
                                >
                                  {values.showPassword ? (
                                    <VisibilityOffIcon />
                                  ) : (
                                    <VisibilityIcon />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Field
                        size="small"
                        component={TextField}
                        name="confirmPassword"
                        type={values.showPassword ? undefined : 'password'}
                        label="Confirm Password"
                        disabled={isLoading}
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          width: '100%',
                          '& > div': {
                            width: '100% !important',
                            maxWidth: '100% !important',
                          },
                        }}
                      >
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={recaptchaSiteKey}
                          onChange={handleCaptchaChange}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isLoading}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Register'}
                  </Button>

                  <Typography
                    variant="body2"
                    sx={{ mt: 2, textAlign: 'center' }}
                  >
                    Already Registered?{' '}
                    <Link component={RouterLink} to="/login">
                      Login here
                    </Link>
                  </Typography>
                </Form>
              )}
            </Formik>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Register;
